import { Component, OnDestroy, AfterContentInit, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { NgbActiveModal, } from '@ng-bootstrap/ng-bootstrap';
import { LocationsFacadeService } from '@services/locations/locations-facade.service';
import { EmployeeDTO, LocationModel } from '@models/index';
import { first, never, Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { CampaignsFacadeService } from '@services/campaigns/campaigns-facade.service';

@Component({
  selector: 'app-employee-form',
  templateUrl: './employee-form.component.html',
  styleUrls: ['./employee-form.component.sass']
})
export class EmployeeFormComponent implements OnInit, AfterContentInit, OnDestroy {
  @Output() createEmployee = new EventEmitter<EmployeeDTO>();
  @Output() updateEmployee = new EventEmitter<EmployeeDTO>();
  @Input() employee = {
    id: '',
    name: '',
    firstName: '',
    lastName: '',
    title: ''
  };
  @Input() isCreated = true;
  @Input() locationId: string = '';
  @Input() campaignId: string = '';
  _subs: Subscription = never().subscribe();
  employeeForm: FormGroup;

  // New: dialog state for PrimeNG modal
  showResultDialog = false;
  resultTitle = '';
  resultMessage = '';
  resultSuccess = false;

  // New: loading state for spinner
  isLoading = false;

  // New: keep reference to created employee so we can return it when closing modal
  lastCreatedEmployee: EmployeeDTO | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private locationsFacadeService: LocationsFacadeService,
    private fb: FormBuilder,
    private campaignsFacadeService: CampaignsFacadeService
  ) { }

  ngOnInit(): void {
    console.log(this.employee);

    if (this.employee.firstName == '' || this.employee.firstName == null || this.employee.firstName == undefined) {
      this.employee.firstName = this.getFirstNameFromFull(this.employee.name);
    }
    if (this.employee.lastName == '' || this.employee.lastName == null || this.employee.lastName == undefined) {
      this.employee.lastName = this.getLastNameFromFull(this.employee.name);
    }
    this.employeeForm = this.fb.group({
      // prefer explicit firstName/lastName, fallback to parsed values from fullName
      firstName: [
        this.employee.firstName,
        [Validators.required, this.noBlankValidator]
      ],
      lastName: [
        this.employee.lastName,
        [Validators.required, this.noBlankValidator]
      ],
      title: [this.employee.title]
    });
  }

  // Extract first name = first token of full name (or empty string)
  private getFirstNameFromFull(fullName: string | undefined | null): string {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    return parts.length > 0 ? parts[0] : '';
  }

  // Extract last name = second token of full name if present, otherwise empty string
  private getLastNameFromFull(fullName: string | undefined | null): string {
    if (!fullName) return '';
    const parts = fullName.trim().split(/\s+/).filter(Boolean);
    return parts.length > 1 ? parts[1] : '';
  }

  noBlankValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value && control.value.trim().length === 0) {
      return { blank: true };
    }
    return null;
  }

  ngAfterContentInit(): void {}

  save(): void {
    // mark controls touched so validation messages show if invalid
    this.employeeForm.markAllAsTouched();
    if (this.employeeForm.invalid) {
      return;
    }

    // trim values to remove surrounding spaces
    const firstName = (this.employeeForm.value.firstName || '').trim();
    const lastName = (this.employeeForm.value.lastName || '').trim();
    const title = (this.employeeForm.value.title || '').trim();
    // update form values with trimmed values (optional)
    this.employeeForm.patchValue({ firstName, lastName, title });

    // show spinner
    this.isLoading = true;

    // removed premature this.activeModal.dismiss();

    this.locationsFacadeService.createEmployee({
      locationId: this.locationId,
      firstName,
      lastName,
      title,
      campaignId: this.campaignId
    }).pipe(first())
      .subscribe({
        next: (response) => {
          // hide spinner and show success dialog
          this.isLoading = false;
          this.resultSuccess = true;
          this.resultTitle = 'Success';
          this.resultMessage = 'Employee created successfully.';
          // store response so it can be returned when modal is closed
          this.lastCreatedEmployee = response;
          this.showResultDialog = true;
          // still emit for any listeners
          this.createEmployee.emit(response);
        },
        error: (err) => {
          // hide spinner and show error dialog
          this.isLoading = false;
          this.resultSuccess = false;
          this.resultTitle = 'Error';
          this.resultMessage = err?.message || 'Failed to create employee. Please try again.';
          this.showResultDialog = true;
        }
      });
  }
  update(): void {
    // mark controls touched so validation messages show if invalid
    this.employeeForm.markAllAsTouched();
    if (this.employeeForm.invalid) {
      return;
    }

    // trim values to remove surrounding spaces
    const firstName = (this.employeeForm.value.firstName || '').trim();
    const lastName = (this.employeeForm.value.lastName || '').trim();
    const title = (this.employeeForm.value.title || '').trim();
    // update form values with trimmed values (optional)
    this.employeeForm.patchValue({ firstName, lastName, title });

    // show spinner
    this.isLoading = true;

    this.locationsFacadeService.updateEmployee({
      employeeId: this.employee.id,
      firstName,
      lastName,
      title,
      campaignId: this.campaignId
    }).pipe(first())
      .subscribe({
        next: (response) => {
          // hide spinner and show success dialog
          this.isLoading = false;
          this.resultSuccess = true;
          this.resultTitle = 'Success';
          this.resultMessage = 'Employee updated successfully.';

          // store response so it can be returned when modal is closed
          this.lastCreatedEmployee = response;
          this.showResultDialog = true;
          // still emit for any listeners
          this.updateEmployee.emit(response);
        },
        error: (err) => {
          // hide spinner and show error dialog
          this.isLoading = false;
          this.resultSuccess = false;
          this.resultTitle = 'Error';
          this.resultMessage = err?.message || 'Failed to update employee. Please try again.';
          this.showResultDialog = true;
        }
      });
  }

  // Called by dialog OK button
  closeResultDialog(): void {
    this.showResultDialog = false;
    if (this.resultSuccess) {
      // return the created employee as the modal result
      this.activeModal.close(this.lastCreatedEmployee);
    }
  }  
  ngOnDestroy(): void {
    if (this._subs) this._subs.unsubscribe()
  }
}


